import React, { useRef } from 'react';
import type { Chart as ChartJS } from 'chart.js';
import moment from 'moment';
import useGym from 'helpers/use_gym';
import { tooltipStyles } from 'react/components/charts/types/chart_types';

type DataPoint = {
  x: string | Date | number;
  y: number;
};

type LineChartParams = {
  lineColor?: string;
  yFormat?: string;
  xFormat?: string;
  tooltipXFormat?: string;
  fillArea?: boolean;
  lineType?: 'linear' | 'monotone';
  currency?: [string, string];
  margin?: { top?: number; left?: number; right?: number; bottom?: number };
  yAxis?: [number] | [number, number];
  maxTicksLimit?: number;
};

type UseLineChartReturn = {
  chartRef: React.MutableRefObject<ChartJS<'line'> | null>;
  chartData: any;
  chartOptions: any;
};

export const useLineChart = (data: DataPoint[], params: LineChartParams): UseLineChartReturn => {
  const {
    lineColor = '#94bf1f',
    yFormat = ',2f',
    xFormat = '%b',
    tooltipXFormat,
    fillArea = false,
    lineType = 'linear',
    currency,
    margin = { top: 20, left: 40, right: 15, bottom: 20 },
    yAxis,
    maxTicksLimit = 10,
  } = params;

  const chartRef = useRef<ChartJS<'line'> | null>(null);
  const gym = useGym();

  const isCurrency = !!currency;
  const currencySymbol = currency?.[0] || gym.currency_symbol;

  const formatYAxisValue = (value: number): string => {
    if (isCurrency) {
      if (value === 0) {
        return `${currencySymbol}0`;
      }
      if (value >= 1000) {
        return `${currencySymbol}${(value / 1000).toFixed(0)}k`;
      }
      return `${currencySymbol}${value.toFixed(0)}`;
    }

    if (yFormat.includes('d')) {
      return Math.round(value).toString();
    }

    const decimals = yFormat.match(/(\d+)f/)?.[1] || '0';
    const decimalCount = parseInt(decimals);
    const isWholeNumber = value % 1 === 0;
    const formatted = value.toFixed(isWholeNumber ? 0 : decimalCount);

    if (yFormat.startsWith(',')) {
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    return formatted;
  };

  const formatTooltipValue = (value: number): string => {
    if (isCurrency) {
      if (value === 0) {
        return `${currencySymbol}0`;
      }
      const decimals = yFormat.match(/(\d+)f/)?.[1] || '2';
      const decimalCount = parseInt(decimals);
      const isWholeNumber = value % 1 === 0;
      const formatted = value.toFixed(isWholeNumber ? 0 : decimalCount);
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `${currencySymbol}${parts.join('.')}`;
    }

    if (yFormat.includes('d')) {
      return Math.round(value).toString();
    }

    const decimals = yFormat.match(/(\d+)f/)?.[1] || '0';
    const decimalCount = parseInt(decimals);
    const isWholeNumber = value % 1 === 0;
    const formatted = value.toFixed(isWholeNumber ? 0 : decimalCount);
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const convertFormat = (format: string): string => {
    return format
      .replace(/%b/g, 'MMM')
      .replace(/%d/g, 'DD')
      .replace(/%Y/g, 'YYYY')
      .replace(/%m/g, 'MM')
      .replace(/%e/g, 'D');
  };

  const xFormatMoment = convertFormat(xFormat);
  const tooltipXFormatMoment = convertFormat(tooltipXFormat || xFormat);

  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const sortedData = [...data].sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());
  const chartData = sortedData.map((point) => point.y);
  const labels = sortedData.map((point) => moment(point.x).format(xFormatMoment));
  const rawDates = sortedData.map((point) => point.x);

  const chartDataConfig = {
    labels,
    datasets: [
      {
        data: chartData,
        borderColor: lineColor,
        backgroundColor: fillArea
          ? (context) => {
              const chart = context.chart;
              if (!chart.chartArea) return lineColor;
              const { top, bottom } = chart.chartArea;
              const ctx = chart.ctx;
              const gradient = ctx.createLinearGradient(0, top, 0, bottom);
              gradient.addColorStop(0, hexToRgba(lineColor, 0.15));
              gradient.addColorStop(1, hexToRgba(lineColor, 0));
              return gradient;
            }
          : 'transparent',
        tension: lineType === 'monotone' ? 0.4 : 0,
        borderWidth: 2,
        fill: fillArea,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointBackgroundColor: lineColor,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverBorderWidth: 2,
        pointHoverBackgroundColor: lineColor,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 2,
    layout: {
      padding: margin,
    },
    interaction: {
      mode: 'nearest' as const,
      intersect: false,
      axis: 'x' as const,
    },
    scales: {
      y: {
        display: true,
        beginAtZero: yAxis ? false : true,
        min: yAxis?.[0],
        max: yAxis?.[1],
        grace: '2%',
        ticks: {
          font: {
            size: 11,
          },
          color: '#666',
          maxTicksLimit: 6,
          callback: (value: number | string) => formatYAxisValue(Number(value)),
        },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      x: {
        display: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#666',
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          maxTicksLimit: maxTicksLimit,
        },
      },
    },
    plugins: {
      tooltip: {
        ...tooltipStyles,
        enabled: true,
        mode: 'nearest' as const,
        intersect: false,
        animation: { duration: 0 },
        callbacks: {
          title: (context: any) => {
            const index = context[0]?.dataIndex;
            const date = rawDates[index];
            return date ? moment(date).format(tooltipXFormatMoment) : '';
          },
          label: (context: any) => {
            const value = context.parsed.y;
            return formatTooltipValue(value);
          },
        },
        displayColors: false,
      },
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
    },
  };

  return {
    chartRef,
    chartData: chartDataConfig,
    chartOptions,
  };
};

export default useLineChart;
